import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { RegistrationsPoint, RegistrationsSeries } from "@/types/statistics";
import { DashboardGraph } from "@/types/referals";
import { useDashboardStore } from "@/store/dashboardStore";
import { DateRange } from "@/store/statisticsPeriodStore";
import {
  parseApiDate,
  startOfDay,
} from "@/components/PeriodSelector/periodDates";
import { buildChartBuckets } from "./chartBuckets";
import cls from "./styles.module.scss";

const WIDTH = 296;
const HEIGHT = 146;
const PAD_LEFT = 26;
const PAD_RIGHT = 6;
const PAD_TOP = 10;
const PAD_BOTTOM = 26;
const AXIS_COLOR = "#505068";

// линии графика — по сервисам из registration_series
const SERVICES: {
  id: keyof Omit<DashboardGraph, "date">;
  label: string;
  color: string;
}[] = [
  { id: "eats", label: "Еда", color: "#c08a1c" },
  { id: "taxi", label: "Такси", color: "#ffc857" },
  { id: "delivery", label: "Доставка", color: "#e8743b" },
];

const TOTAL_SERVICE = {
  id: "total" as const,
  label: "Все сервисы",
  color: "#c08a1c",
};

const sum = (values: number[]) => values.reduce((acc, v) => acc + v, 0);

// верх шкалы — круглое чётное число, чтобы середина тоже была целой
const niceScale = (value: number) => {
  const half = value / 2;
  const step = Math.pow(10, Math.floor(Math.log10(Math.max(half, 1))));
  return Math.max(Math.ceil(half / step) * step, 1) * 2;
};

export const RegistrationsChart = () => {
  const dashboardInfo = useDashboardStore((s) => s.dashboardInfo);

  const rows = useMemo(() => dashboardInfo?.registration_series ?? [], [
    dashboardInfo,
  ]);

  // строки с нераспознанной датой в график не берём
  const points = useMemo(
    () =>
      rows
        .map((row) => ({ row, date: parseApiDate(row.date) }))
        .filter((item): item is { row: DashboardGraph; date: Date } =>
          Boolean(item.date)
        ),
    [rows]
  );

  const series = useMemo<RegistrationsSeries[]>(() => {
    const toPoints = (key: keyof Omit<DashboardGraph, "date">) =>
      points.map<RegistrationsPoint>(({ row, date }) => ({
        date,
        value: Number(row[key]) || 0,
      }));

    // показываем только сервисы, по которым за период кто-то пришёл
    const active = SERVICES.filter(({ id }) =>
      points.some(({ row }) => Number(row[id]) > 0)
    );

    // никого нет — одна линия по total, чтобы блок не прыгал
    const lines = active.length ? active : [TOTAL_SERVICE];

    return lines.map(({ id, label, color }) => ({
      id,
      label,
      color,
      points: toPoints(id),
    }));
  }, [points]);

  const today = useMemo(() => startOfDay(new Date()), []);

  // период графика — тот же, что вернул бэк; без него берём крайние даты серии
  const range = useMemo<DateRange>(() => {
    const from = parseApiDate(dashboardInfo?.period.from);
    const to = parseApiDate(dashboardInfo?.period.to);
    if (from && to) return { from, to };

    const times = points.map(({ date }) => date.getTime());
    if (!times.length) return { from: today, to: today };

    return {
      from: from ?? new Date(Math.min(...times)),
      to: to ?? new Date(Math.max(...times)),
    };
  }, [dashboardInfo, points, today]);

  const fromTime = range.from.getTime();
  const toTime = range.to.getTime();

  const { labels, tips, series: buckets } = useMemo(
    () =>
      buildChartBuckets(
        { from: new Date(fromTime), to: new Date(toTime) },
        series
      ),
    [fromTime, toTime, series]
  );

  const [isTipOpen, setIsTipOpen] = useState(false);
  // столбец подсказки не сбрасываем при закрытии, чтобы она гасла с содержимым
  const [tipIndex, setTipIndex] = useState(0);

  useEffect(() => {
    setIsTipOpen(false);
  }, [fromTime, toTime, series]);

  // тап в любом месте вне столбца прячет подсказку
  useEffect(() => {
    if (!isTipOpen) return;
    const hide = () => setIsTipOpen(false);
    document.addEventListener("click", hide);
    return () => document.removeEventListener("click", hide);
  }, [isTipOpen]);

  const count = labels.length;
  const max = niceScale(Math.max(1, ...buckets.flatMap((s) => s.values)));
  const columnWidth = (WIDTH - PAD_LEFT - PAD_RIGHT) / Math.max(count - 1, 1);

  const x = (i: number) => PAD_LEFT + i * columnWidth;
  const y = (v: number) =>
    PAD_TOP + (1 - v / max) * (HEIGHT - PAD_TOP - PAD_BOTTOM);

  const total = sum(buckets.flatMap((s) => s.values));

  // подсказка не вылезает за края графика
  const tipLeft = Math.min(Math.max((x(tipIndex) / WIDTH) * 100, 22), 78);
  const tipRows = buckets.map((s) => ({
    ...s,
    value: s.values[tipIndex] ?? 0,
  }));

  if (!dashboardInfo) return null;

  return (
    <div className={cls.block}>
      <div className={cls.blockHead}>
        <div className={cls.blockTitle}>Регистрации по дням</div>
        <div className={cls.blockSubtitle}>
          {total.toLocaleString("ru-RU")} за период
        </div>
      </div>

      <div className={cls.chartWrap}>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          width="100%"
          height={HEIGHT}
          className={cls.chart}
        >
          {[0, max / 2, max].map((v) => (
            <g key={v}>
              <line
                x1={PAD_LEFT}
                y1={y(v).toFixed(1)}
                x2={WIDTH - PAD_RIGHT}
                y2={y(v).toFixed(1)}
                stroke={`rgba(255, 255, 255, ${v === 0 ? 0.1 : 0.05})`}
                strokeWidth="1"
              />
              <text
                x={PAD_LEFT - 7}
                y={(y(v) + 3.5).toFixed(1)}
                fill={AXIS_COLOR}
                fontSize="10"
                textAnchor="end"
              >
                {v}
              </text>
            </g>
          ))}

          {buckets.map((s) => {
            const points = s.values.map((v, i) => [x(i), y(v)]);
            const path = points
              .map(
                ([px, py], i) =>
                  `${i ? "L" : "M"}${px.toFixed(1)} ${py.toFixed(1)}`
              )
              .join(" ");

            return (
              <g key={s.id}>
                <path
                  d={path}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                {points.map(([px, py], i) => (
                  <circle
                    key={i}
                    cx={px.toFixed(1)}
                    cy={py.toFixed(1)}
                    r="4"
                    fill={s.color}
                    stroke="#131320"
                    strokeWidth="2"
                  />
                ))}
              </g>
            );
          })}

          {labels.map((label, i) => {
            if (!label) return null;
            const anchor =
              i === 0 ? "start" : i === count - 1 ? "end" : "middle";
            return (
              <text
                key={i}
                x={x(i).toFixed(1)}
                y={HEIGHT - 7}
                fill={AXIS_COLOR}
                fontSize="11"
                textAnchor={anchor}
              >
                {label}
              </text>
            );
          })}

          {labels.map((_, i) => (
            <rect
              key={i}
              x={(x(i) - columnWidth / 2).toFixed(1)}
              y="0"
              width={columnWidth.toFixed(1)}
              height={HEIGHT - PAD_BOTTOM + 6}
              fill="transparent"
              className={cls.column}
              onClick={(e) => {
                e.stopPropagation();
                setTipIndex(i);
                setIsTipOpen(true);
              }}
            />
          ))}
        </svg>

        <div
          className={classNames(cls.tip, isTipOpen && cls.tipOn)}
          style={{ left: `${tipLeft}%` }}
        >
          <div className={cls.tipDate}>
            {tips[tipIndex] || labels[tipIndex]}
          </div>
          {tipRows.map((row) => (
            <div key={row.id} className={cls.tipRow}>
              <i style={{ background: row.color }} />
              {row.label}
              <b>{row.value}</b>
            </div>
          ))}
          <div className={classNames(cls.tipRow, cls.tipSum)}>
            Всего
            <b>{sum(tipRows.map((row) => row.value))}</b>
          </div>
        </div>
      </div>

      <div className={cls.legend}>
        {buckets.map((s) => (
          <span key={s.id}>
            <i style={{ background: s.color }} />
            {s.label} · {sum(s.values).toLocaleString("ru-RU")}
          </span>
        ))}
      </div>
    </div>
  );
};
